
import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Mic, Copy, Save, Trash2, Download, Upload, Wand2, Zap, BookOpen, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { TEMPLATE_LIBRARY, refinePromptLogic, fillTemplate, type Template, type RefinementResult } from './lib/engine';
import { aiEnhancePrompt } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

interface SavedPrompt { id: string; title: string; raw: string; refined: string; aiEnhanced?: string; category: string; date: string; }

export default function App() {
  const [raw, setRaw] = useState('');
  const [result, setResult] = useState<RefinementResult | null>(null);
  const [aiOutput, setAiOutput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [saved, setSaved] = useState<SavedPrompt[]>(() => JSON.parse(localStorage.getItem('ca_prompts') || '[]'));
  const [activeTab, setActiveTab] = useState<'logic' | 'ai'>('logic');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [templateValues, setTemplateValues] = useState<Record<string, string>>({});
  const [isRecording, setIsRecording] = useState(false);
  const aiAbortRef = useRef<AbortController | null>(null);

  useEffect(() => { localStorage.setItem('ca_prompts', JSON.stringify(saved)); }, [saved]);

  const handleRefine = () => {
    if (!raw.trim()) return;
    const res = refinePromptLogic(raw);
    setResult(res);
    setAiOutput('');
    setActiveTab('logic');
    if (res.template) {
      setSelectedTemplate(res.template);
      const vals: Record<string, string> = {};
      res.template.fields.forEach(f => vals[f] = '');
      setTemplateValues(vals);
    }
  };

  const handleAiEnhance = async () => {
    if (!result) return;
    setIsAiLoading(true);
    setAiOutput('');
    setActiveTab('ai');
    let text = '';
    await aiEnhancePrompt(raw, result.refined, (chunk) => {
      text += chunk;
      setAiOutput(text);
    });
    setIsAiLoading(false);
  };

  const handleSave = () => {
    if (!result) return;
    const item: SavedPrompt = {
      id: Date.now().toString(),
      title: raw.slice(0, 40) + (raw.length > 40 ? '...' : ''),
      raw, refined: result.refined, aiEnhanced: aiOutput || undefined,
      category: result.category || 'general', date: new Date().toLocaleDateString()
    };
    setSaved(prev => [item, ...prev]);
  };

  const handleDelete = (id: string) => setSaved(prev => prev.filter(p => p.id !== id));

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  const exportLibrary = () => {
    const blob = new Blob([JSON.stringify(saved, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'catalystagile-prompt-library.json'; a.click();
  };

  const importLibrary = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result as string);
        if (Array.isArray(data)) setSaved(data);
      } catch {}
    };
    reader.readAsText(file);
  };

  const startDictation = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert('Browser speech recognition not supported. Use Chrome/Edge.'); return; }
    const rec = new SR();
    rec.continuous = false; rec.interimResults = false;
    rec.onstart = () => setIsRecording(true);
    rec.onend = () => setIsRecording(false);
    rec.onresult = (e: any) => { setRaw(prev => prev + ' ' + e.results[0][0].transcript); };
    rec.start();
  };

  const scoreColor = (s: number) => s >= 80 ? 'text-green-400' : s >= 60 ? 'text-yellow-400' : 'text-red-400';
  const scoreIcon = (s: number) => s >= 80 ? <CheckCircle className="w-5 h-5 text-green-400"/> : s >= 60 ? <AlertTriangle className="w-5 h-5 text-yellow-400"/> : <XCircle className="w-5 h-5 text-red-400"/>;

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-[#f0f0f0]">CatalystAgile <span className="text-[#d4af37]">AI Prompt Refiner</span></h1>
              <p className="text-xs text-[#8a9bb8]">Logic-First Refinement + Optional AI Enhancement</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportLibrary} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 flex items-center gap-1.5 transition-colors">
              <Download className="w-3.5 h-3.5"/> Export
            </button>
            <label className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 flex items-center gap-1.5 transition-colors cursor-pointer">
              <Upload className="w-3.5 h-3.5"/> Import
              <input type="file" accept=".json" className="hidden" onChange={importLibrary}/>
            </label>
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Template Library */}
        <aside className="lg:col-span-3 space-y-3">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4"/> Template Library</h2>
            <div className="space-y-2">
              {TEMPLATE_LIBRARY.map(t => (
                <button key={t.id} onClick={() => { setSelectedTemplate(t); setTemplateValues({}); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${selectedTemplate?.id === t.id ? 'bg-[#d4af37]/20 border border-[#d4af37]/40' : 'bg-[#132238] border border-white/5 hover:bg-[#1a2d4a]'}`}>
                  <div className="font-medium text-[#f0f0f0]">{t.name}</div>
                  <div className="text-[10px] text-[#8a9bb8] uppercase tracking-wider mt-0.5">{t.category}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Save className="w-4 h-4"/> Saved Library ({saved.length})</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {saved.map(p => (
                <div key={p.id} className="group bg-[#132238] rounded-lg p-2.5 border border-white/5 hover:border-[#d4af37]/30 transition-colors">
                  <div className="flex items-start justify-between">
                    <button onClick={() => { setRaw(p.raw); setResult(refinePromptLogic(p.raw)); setAiOutput(p.aiEnhanced || ''); }}
                      className="text-left text-xs text-[#f0f0f0] font-medium line-clamp-2 flex-1 hover:text-[#d4af37] transition-colors">
                      {p.title}
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#0a1628] text-[#8a9bb8] uppercase">{p.category}</span>
                    <span className="text-[10px] text-[#8a9bb8]">{p.date}</span>
                  </div>
                </div>
              ))}
              {saved.length === 0 && <p className="text-xs text-[#8a9bb8] italic">No saved prompts yet.</p>}
            </div>
          </div>
        </aside>

        {/* CENTER: Input & Results */}
        <section className="lg:col-span-9 space-y-4">
          {/* Input */}
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-[#f0f0f0]">Raw Prompt Input</label>
              <div className="flex items-center gap-2">
                <button onClick={startDictation} className={`p-2 rounded-lg transition-colors ${isRecording ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-[#132238] text-[#8a9bb8] hover:text-[#d4af37]'}`}>
                  <Mic className="w-4 h-4"/>
                </button>
                <button onClick={handleRefine} className="px-4 py-2 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors flex items-center gap-2">
                  <Zap className="w-4 h-4"/> Refine Prompt
                </button>
              </div>
            </div>
            <textarea value={raw} onChange={e => setRaw(e.target.value)}
              placeholder="Paste your rough prompt here... e.g., 'Write something about AI'"
              className="w-full h-32 bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50 resize-none font-mono"/>
            {isRecording && <p className="text-xs text-red-400 mt-2 animate-pulse">● Recording... speak now</p>}
          </div>

          {/* Template Builder */}
          {selectedTemplate && (
            <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/20 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#d4af37]">Template Builder: {selectedTemplate.name}</h3>
                <button onClick={() => setSelectedTemplate(null)} className="text-xs text-[#8a9bb8] hover:text-[#f0f0f0]">Close</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {selectedTemplate.fields.map(f => (
                  <div key={f}>
                    <label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">{f}</label>
                    <input value={templateValues[f] || ''} onChange={e => setTemplateValues(prev => ({ ...prev, [f]: e.target.value }))}
                      placeholder={selectedTemplate.example[f] || f}
                      className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50"/>
                  </div>
                ))}
              </div>
              <button onClick={() => { const filled = fillTemplate(selectedTemplate, templateValues); setRaw(filled); handleRefine(); }}
                className="px-4 py-2 rounded-lg bg-[#132238] border border-[#d4af37]/30 text-[#d4af37] text-sm font-medium hover:bg-[#d4af37]/10 transition-colors">
                Fill Template & Refine
              </button>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 overflow-hidden">
              {/* Score Bar */}
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-4 bg-[#070f1a]/50">
                <div className="flex items-center gap-2">
                  {scoreIcon(result.score)}
                  <span className="text-2xl font-bold ${scoreColor(result.score)}">{result.score}</span>
                  <span className="text-xs text-[#8a9bb8]">/ 100</span>
                </div>
                <div className="flex-1 h-2 bg-[#132238] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${result.score}%`, backgroundColor: result.score >= 80 ? '#22c55e' : result.score >= 60 ? '#f59e0b' : '#ef4444' }}/>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleSave} className="p-2 rounded-lg bg-[#132238] hover:bg-[#1a2d4a] text-[#8a9bb8] hover:text-[#d4af37] transition-colors">
                    <Save className="w-4 h-4"/>
                  </button>
                  <button onClick={handleAiEnhance} disabled={isAiLoading}
                    className="px-4 py-2 rounded-lg bg-[#132238] border border-[#d4af37]/30 text-[#d4af37] text-sm font-medium hover:bg-[#d4af37]/10 transition-colors flex items-center gap-2 disabled:opacity-50">
                    <Sparkles className="w-4 h-4"/> {isAiLoading ? 'Enhancing...' : 'AI Enhance'}
                  </button>
                </div>
              </div>

              {/* Issues */}
              {result.issues.length > 0 && (
                <div className="px-4 py-3 border-b border-white/10">
                  <h3 className="text-xs font-semibold text-[#8a9bb8] uppercase tracking-wider mb-2">Issues Found ({result.issues.length})</h3>
                  <div className="space-y-1.5">
                    {result.issues.map((issue, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-[#f0f0f0]">
                        <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 mt-0.5 shrink-0"/>
                        {issue}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex border-b border-white/10">
                <button onClick={() => setActiveTab('logic')} className={`px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === 'logic' ? 'text-[#d4af37] border-b-2 border-[#d4af37]' : 'text-[#8a9bb8] hover:text-[#f0f0f0]'}`}>
                  Logic Refinement
                </button>
                <button onClick={() => setActiveTab('ai')} className={`px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === 'ai' ? 'text-[#d4af37] border-b-2 border-[#d4af37]' : 'text-[#8a9bb8] hover:text-[#f0f0f0]'}`}>
                  AI Enhanced {aiOutput && '✓'}
                </button>
              </div>

              <div className="p-4">
                {activeTab === 'logic' ? (
                  <div className="relative">
                    <pre className="bg-[#070f1a] rounded-lg p-4 text-sm text-[#f0f0f0] font-mono whitespace-pre-wrap border border-white/10">{result.refined}</pre>
                    <button onClick={() => copyToClipboard(result.refined)} className="absolute top-3 right-3 p-1.5 rounded bg-[#132238] text-[#8a9bb8] hover:text-[#d4af37] transition-colors">
                      <Copy className="w-4 h-4"/>
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <pre className="bg-[#070f1a] rounded-lg p-4 text-sm text-[#f0f0f0] font-mono whitespace-pre-wrap border border-[#d4af37]/20 min-h-[120px]">
                      {aiOutput || (isAiLoading ? 'Thinking...' : 'Click "AI Enhance" to generate an enhanced version.')}
                    </pre>
                    {aiOutput && (
                      <button onClick={() => copyToClipboard(aiOutput)} className="absolute top-3 right-3 p-1.5 rounded bg-[#132238] text-[#8a9bb8] hover:text-[#d4af37] transition-colors">
                        <Copy className="w-4 h-4"/>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
